import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from './api.js';
import type { Issuance, Order } from '@z9-store/core';

interface OrdersResponse {
  readonly orders: ReadonlyArray<Order>;
}

export interface LicenseListItem {
  readonly issuance: Issuance & { readonly licenseBlobS3Key?: string };
  readonly expired: boolean;
  readonly expiringSoon: boolean;
}

interface LicensesResponse {
  readonly licenses: ReadonlyArray<LicenseListItem>;
}

interface RenewResponse {
  readonly orderId: string;
  readonly checkoutUrl: string;
}

export function useOrders() {
  return useQuery<ReadonlyArray<Order>>({
    queryKey: ['orders'],
    queryFn: async () => (await api.get<OrdersResponse>('/orders')).orders,
    staleTime: 10_000
  });
}

export function useLicenses() {
  return useQuery<ReadonlyArray<LicenseListItem>>({
    queryKey: ['licenses'],
    queryFn: async () => (await api.get<LicensesResponse>('/licenses')).licenses,
    staleTime: 10_000
  });
}

export function useRenew() {
  return useMutation<RenewResponse, Error, ReadonlyArray<string>>({
    mutationFn: (issuanceIds) => api.post<RenewResponse>('/checkout/renew', { issuanceIds })
  });
}

// Truncate a fingerprint hex or similar long binding string for table display.
export function shortBinding(value: string): string {
  if (value.length <= 16) return value;
  return value.slice(0, 8) + '...' + value.slice(-6);
}

// Human ISO -> "YYYY-MM-DD" for the table; tiny utility so the page doesn't
// pull in date-fns just to slice 10 chars.
export function shortDate(iso: string): string {
  return iso.slice(0, 10);
}
